import React from "react";
import type { ApolloClient } from "@apollo/client";
import { size } from "lodash";
import moment from "moment";
import { Card, Col, Row } from "react-bootstrap";
import { FiDownload, FiEye } from "react-icons/fi";
import { isProduction } from "utils/determineSite";

import IconTableHeader from "components/IconTableHeader";
import IconBanner from "components/IconBanner";
import IconTable, { type IconTableColumn } from "components/IconTable";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { employerConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { connect, type ConnectedProps } from "react-redux";
import { getCompanyAgreements } from "actions/employerActions";
import {
  getEmployerDocuments,
  getEmployerDownloadUrl,
} from "services/documentService";
import { simulateLinkClickNewTab } from "utils/dom";
import IconSpinner from "components/IconSpinner";
import TermsModal from "pages/signUp/TermsModal";
import { getEmployerCRBAgreementDate } from "store/selectors/employer";
import type { GetEmployerDocumentsQuery } from "types/graphql";
import type { RootState } from "types/state";

interface DocumentRow {
  name: string;
  id?: string;
  url: string;
  docType: string;
}

type EmployerStatement = NonNullable<
  NonNullable<GetEmployerDocumentsQuery["getEmployerDocuments"]>[number]
>;

// Document.type is a backend-provided string (schema declares it String!);
// enrollment flyers come back with the literal value "EnrollmentFlyer".
const ENROLLMENT_FLYER_TYPE = "EnrollmentFlyer";

const PRP_STATEMENTS_BANNER = (
  <>
    All contributions to your Icon PRP employee&apos;s accounts.
    <br />
    <br />
    <strong>IMPORTANT</strong>: These statements will always balance to zero
    dollars as contributions pass through this account and are sent to your
    participating employee&apos;s retirement accounts.
  </>
);

const iconDocuments: DocumentRow[] = [
  {
    name: "Icon's current W9",
    id: "icon-current-W9",
    url: `https://iconsavingsplan-public.s3.us-west-2.amazonaws.com/${
      isProduction() ? "production" : "staging"
    }/documents/ICON_FORM_W9.pdf`,
    docType: "download",
  },
  {
    name: "Employee onboarding PDF",
    id: "employee-onboarding-PDF",
    url: `https://iconsavingsplan-public.s3.us-west-2.amazonaws.com/${
      isProduction() ? "production" : "staging"
    }/documents/ICON_EMPLOYEE_ONBOARDING.pdf`,
    docType: "download",
  },
  {
    name: "Plan announcement email template",
    id: "icon-announcement-template-PDF",
    url: `https://iconsavingsplan-public.s3.us-west-2.amazonaws.com/${
      isProduction() ? "production" : "staging"
    }/documents/ICON_ANNOUNCEMENT_TEMPLATE.pdf`,
    docType: "download",
  },
  {
    name: "Icon email template",
    id: "icon-email-template-PDF",
    url: `https://iconsavingsplan-public.s3.us-west-2.amazonaws.com/${
      isProduction() ? "production" : "staging"
    }/documents/ICON_EMAIL_TEMPLATE.pdf`,
    docType: "download",
  },
  {
    name: "Icon email template - State Plan",
    id: "icon-email-template-state-plan-PDF",
    url: `https://iconsavingsplan-public.s3.us-west-2.amazonaws.com/${
      isProduction() ? "production" : "staging"
    }/documents/ICON_EMAIL_TEMPLATE_STATE_PLAN.pdf`,
    docType: "download",
  },
];

const iconVideos: DocumentRow[] = [
  {
    name: "Introduction to Icon",
    url: `https://vimeo.com/1125367794`,
    docType: "video",
  },
  {
    name: "Getting Started with Your Icon Retirement Account",
    url: `https://vimeo.com/1125348959`,
    docType: "video",
  },
  {
    name: "Making Contributions through Your Paycheck",
    url: `https://vimeo.com/1125360724`,
    docType: "video",
  },
  {
    name: "Your Icon Account is Portable",
    id: "icon-announcement-template-PDF",
    url: `https://vimeo.com/1125584786`,
    docType: "video",
  },
  {
    name: "Savings Vehicle",
    url: `https://vimeo.com/1125580389`,
    docType: "video",
  },
  {
    name: "Tour of the PRP by Icon",
    url: `https://iconsavingsplan.com/product-tour/`,
    docType: "video",
  },
  {
    name: "Introducción a Icon",
    url: `https://vimeo.com/1125578903`,
    docType: "video",
  },
  {
    name: "Cómo comenzar con tu cuenta de retiro de Icon",
    url: `https://vimeo.com/1125352090`,
    docType: "video",
  },
  {
    name: "Hacer contribuciones a través de tu nómina",
    url: `https://vimeo.com/1125361052`,
    docType: "video",
  },
  {
    name: "Tu cuenta de Icon es portátil",
    id: "icon-announcement-template-PDF",
    url: `https://vimeo.com/1126256752`,
    docType: "video",
  },
  {
    name: "Vehículo de ahorro",
    url: `https://vimeo.com/1125582805`,
    docType: "video",
  },
];

interface AgreementRow {
  createdAt: string;
  name: string;
  id: string;
  onClick: () => void;
}

const actions = [employerConstants.GET_COMPANY_AGREEMENTS];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
    error: errorSelector(state),
    crbAgreementDate: getEmployerCRBAgreementDate(state),
  };
};

const mapDispatchToProps = {
  getCompanyAgreements,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  // termsAcceptanceDate is set in initial state but never written via setState.
  termsAcceptanceDate: string | null;
  showCRBAgreement: boolean;
  prpStatements: EmployerStatement[];
  enrollmentDocuments: EmployerStatement[];
}

export class EmployerDocuments extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      termsAcceptanceDate: null,
      showCRBAgreement: false,
      prpStatements: [],
      enrollmentDocuments: [],
    };
  }

  componentDidMount() {
    void this.props.getCompanyAgreements(
      this.props.client as ApolloClient<object>
    );
    void this._fetchPrpStatements();
    void this._fetchEnrollmentDocuments();
  }

  _fetchPrpStatements = async () => {
    try {
      const documents = await getEmployerDocuments(
        this.props.client as ApolloClient<object>,
        "CRB"
      );
      this.setState({
        prpStatements: (documents ?? []).filter(
          (doc): doc is EmployerStatement => doc != null
        ),
      });
    } catch {
      // Non-fatal: the PRP section simply renders empty if the fetch fails.
    }
  };

  _downloadPrpStatement = (documentId: string) => {
    void getEmployerDownloadUrl(this.props.client as ApolloClient<object>, {
      documentId,
    }).then((result) => {
      if (result?.url) {
        simulateLinkClickNewTab(result.url, false);
      }
    });
  };

  _fetchEnrollmentDocuments = async () => {
    try {
      const documents = await getEmployerDocuments(
        this.props.client as ApolloClient<object>,
        "ICON",
        undefined,
        ENROLLMENT_FLYER_TYPE
      );
      this.setState({
        enrollmentDocuments: (documents ?? []).filter(
          (doc): doc is EmployerStatement => doc != null
        ),
      });
    } catch {
      // Non-fatal: the enrollment section simply renders empty if the fetch fails.
    }
  };

  _downloadEnrollmentDocument = (documentId: string) => {
    void getEmployerDownloadUrl(this.props.client as ApolloClient<object>, {
      documentId,
    }).then((result) => {
      if (result?.url) {
        simulateLinkClickNewTab(result.url, false);
      }
    });
  };

  // Enrollment flyers are folded into the static "Documents" table as extra rows. They download
  // through a presigned URL (docType "enrollment") rather than a direct link like the static rows.
  _enrollmentDocumentRows = (): DocumentRow[] => {
    return this.state.enrollmentDocuments.map((doc) => ({
      name: doc.name,
      id: doc.id,
      url: "",
      docType: "enrollment",
    }));
  };

  _buildPrpStatementColumns = (): Array<IconTableColumn<EmployerStatement>> => {
    return [
      {
        label: "Statement Date",
        customComponent: (props) =>
          moment(props.createdAt).format("MMM Do YYYY"),
      },
      {
        label: "Name",
        key: "name",
      },
      {
        customComponent: (props) => (
          <a
            className="link"
            onClick={() => {
              this._downloadPrpStatement(props.id);
            }}
          >
            <FiDownload
              size={"20px"}
              color={"#FFFFFF"}
              fill={"#FFFFFF"}
              stroke={"#635bff"}
              strokeWidth={"1.5"}
              className="icon"
            />
          </a>
        ),
      },
    ];
  };

  _buildDocumentsColumns = (): Array<IconTableColumn<DocumentRow>> => {
    const columns: Array<IconTableColumn<DocumentRow>> = [
      {
        label: "Document Name",
        key: "name",
      },

      {
        customComponent: (props) => {
          // Enrollment flyers live in S3 and need a freshly signed URL, so they download via an
          // async click handler; the static rows are plain links to public assets.
          if (props.docType === "enrollment") {
            return (
              <a
                className="link"
                onClick={() => {
                  if (props.id) {
                    this._downloadEnrollmentDocument(props.id);
                  }
                }}
              >
                <FiDownload
                  size={"20px"}
                  color={"#FFFFFF"}
                  fill={"#FFFFFF"}
                  stroke={"#635bff"}
                  strokeWidth={"1.5"}
                  className="icon"
                />
              </a>
            );
          }
          return (
            <a
              className="link"
              href={props.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiDownload
                size={"20px"}
                color={"#FFFFFF"}
                fill={"#FFFFFF"}
                stroke={"#635bff"}
                strokeWidth={"1.5"}
                className="icon"
              />
            </a>
          );
        },
      },
    ];
    return columns;
  };

  _buildVideosColumns = (): Array<IconTableColumn<DocumentRow>> => {
    const columns: Array<IconTableColumn<DocumentRow>> = [
      {
        label: "Video Title",
        key: "name",
      },

      {
        customComponent: (props) => {
          return (
            <a
              className="terms-link"
              href={props.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                justifyContent: "center",
                color: "#01a3b0",
                cursor: "pointer",
              }}
            >
              <p
                className="terms-text"
                style={{
                  fontStyle: "italic",
                  fontSize: 14,
                  margin: 0,
                }}
              >
                View Video
              </p>
            </a>
          );
        },
      },
    ];
    return columns;
  };

  _buildAgreementsColumns = (): Array<IconTableColumn<AgreementRow>> => {
    const columns: Array<IconTableColumn<AgreementRow>> = [
      {
        label: "Statement Date",
        key: "createdAt",
      },

      {
        label: "Name",
        key: "name",
      },

      {
        customComponent: (props) => {
          return (
            <a className="link" onClick={props.onClick}>
              <FiEye
                size={"20px"}
                color={"#FFFFFF"}
                fill={"#FFFFFF"}
                stroke={"#635bff"}
                strokeWidth={"1.5"}
                className="icon"
              />
            </a>
          );
        },
      },
    ];
    return columns;
  };

  render() {
    const agreements: AgreementRow[] = [
      {
        createdAt: this.props.crbAgreementDate,
        name: "Cross River Bank Deposit Account Agreement",
        id: "crb-agreement",
        onClick: () => {
          this.setState({ showCRBAgreement: true });
        },
      },
    ];

    // Per-group enrollment flyers appear alongside the static Icon documents in one table.
    const documentRows: DocumentRow[] = [
      ...this._enrollmentDocumentRows(),
      ...iconDocuments,
    ];

    return (
      <div className="mega-container dashboard-content" id="documents">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Icon Account Documents</h1>
            <p className="page-subtext">See and download all your documents.</p>
            <div className="main-content">
              {this.state.showCRBAgreement && (
                <TermsModal
                  termsInfo={{}}
                  type={"crbAgreement"}
                  show={true}
                  onClose={() => this.setState({ showCRBAgreement: false })}
                />
              )}
              <Row>
                <Col>
                  <Card className="dashboard-widget statements-table">
                    <>
                      <div className="business-member-documents">
                        <IconTableHeader
                          tableHeader="PRP Statements"
                          tableCount={size(this.state.prpStatements)}
                        />
                        <IconBanner
                          subheader={PRP_STATEMENTS_BANNER}
                          circle={false}
                        />
                        <div className="documents-table-scroll">
                          <IconTable
                            columns={this._buildPrpStatementColumns()}
                            data={this.state.prpStatements}
                          />
                        </div>
                      </div>
                    </>
                  </Card>

                  <Card className="dashboard-widget statements-table">
                    <>
                      <div className="business-member-documents">
                        <IconTableHeader
                          tableHeader="Documents"
                          tableCount={size(documentRows)}
                        />
                        <IconTable
                          columns={this._buildDocumentsColumns()}
                          data={documentRows}
                        />
                      </div>
                    </>
                  </Card>

                  <Card className="dashboard-widget statements-table">
                    <>
                      <div className="business-member-documents">
                        <IconTableHeader
                          tableHeader="Employee Video Links"
                          tableCount={size(iconVideos)}
                        />
                        <IconTable
                          columns={this._buildVideosColumns()}
                          data={iconVideos}
                        />
                      </div>
                    </>
                  </Card>

                  <Card className="dashboard-widget statements-table">
                    {!this.props.isFetching && (
                      <>
                        <div className="business-member-documents">
                          <IconTableHeader
                            tableHeader="Agreements"
                            tableCount={size(agreements)}
                          />
                          <IconTable
                            columns={this._buildAgreementsColumns()}
                            data={agreements}
                          />
                        </div>
                      </>
                    )}
                    {this.props.isFetching && <IconSpinner centered />}
                  </Card>
                </Col>
              </Row>
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerDocuments));
